import { sortTripsForDisplay, buildTripSections } from './tripSections';
import { planDateIso } from './groupLeg';

/**
 * ID string for matching planner `drivers[]` to `original_plan` bucket `driver` objects
 * (fields vary by API: `driver_id`, `id`, `_id`).
 */
export function resolvePlannerDriverIdForPortal(driver) {
  if (!driver) return null;
  const v = driver.driver_id ?? driver.id ?? driver._id;
  if (v == null || v === '') return null;
  return String(v);
}

function portalDriverMatchesId(dr, wantStr) {
  if (!dr) return false;
  const keys = [dr._id, dr.id, dr.driver_id, dr.driverId]
    .map((k) => (k != null && k !== '' ? String(k) : null))
    .filter(Boolean);
  return keys.length > 0 && keys.includes(String(wantStr));
}

/**
 * Trips in `original_plan` for a driver (for date checks, etc.)
 */
export function getPortalPlanTripsForDriver(assignmentData, driverId) {
  const op = assignmentData?.original_plan;
  if (!op) return [];
  const want = String(driverId);
  for (const bucket of op.drivers || []) {
    const dr = bucket.driver || {};
    if (!portalDriverMatchesId(dr, want)) continue;
    return bucket.trips || [];
  }
  return [];
}

/**
 * True if any trip in the list is scheduled on a calendar date before "today" in the given IANA zone.
 * Matches the guard used in the global Publish (TimeFilter) flow.
 */
export function hasPastDatedPlanTrips(trips, timezone) {
  if (!timezone) return false;
  const todayInTz = new Date().toLocaleDateString('en-CA', { timeZone: timezone });
  for (const trip of trips || []) {
    const scheduleIso = planDateIso(trip);
    if (!scheduleIso) continue;
    const tripDateInTz = new Date(scheduleIso).toLocaleDateString('en-CA', { timeZone: timezone });
    if (tripDateInTz < todayInTz) return true;
  }
  return false;
}

/**
 * Build NEMT publish payload from `assignmentData.original_plan` (Portal plan).
 * Mirrors the shape produced for the full-scheduler publish flow.
 *
 * @param {object} assignmentData - session results including `original_plan`
 * @param {string|null|undefined} onlyDriverId - if set, only that driver's bucket (matches portal `driver._id` / `driver.id` to this string)
 * @returns {{ data: Array }}
 */
export function buildPortalPlanPayload(assignmentData, onlyDriverId = null) {
  const payload = { data: [] };
  const op = assignmentData?.original_plan;
  if (!op) {
    throw new Error('No Portal Plan data (original_plan) in this session.');
  }
  const { drivers = [], unassigned_trips = [] } = op;
  const want = onlyDriverId != null && onlyDriverId !== '' ? String(onlyDriverId) : null;

  drivers.forEach((bucket) => {
    const dr = bucket.driver || {};
    if (want != null && !portalDriverMatchesId(dr, want)) {
      return;
    }
    const trips = bucket.trips || [];
    trips.forEach((trip, index) => {
      const tripId = trip._id ?? trip.id;
      if (!tripId) return;
      payload.data.push({
        selectedTrips: [tripId],
        status: 'offered',
        priority: index + 1,
        assignThrough: 'planner',
        driver: {
          _id: dr._id ?? dr.id,
          displayName: dr.displayName ?? dr.driver_name ?? '',
          profileImageURL: dr.profileImageURL ?? '',
        },
      });
    });
  });

  if (want == null && unassigned_trips.length > 0) {
    payload.data.push({
      selectedTrips: unassigned_trips.map((t) => t._id ?? t.id).filter(Boolean),
      status: 'unassigned',
      priority: 0,
      assignThrough: 'planner',
    });
  }
  return payload;
}

/**
 * @param {object} payload - `{ data: [...] }` publish payload
 * @param {string[]} validTripIds - from validate API
 * @returns {Array} filtered `data` rows (trips dropped if invalid)
 */
export function filterPayloadByValidatedTripIds(payload, validTripIds) {
  const validSet = new Set((validTripIds || []).map((id) => String(id)));
  const filtered = [];
  (payload.data || []).forEach((item) => {
    const trips = (item.selectedTrips || []).filter((id) => validSet.has(String(id)));
    if (trips.length === 0) return;
    filtered.push({ ...item, selectedTrips: trips });
  });
  return filtered;
}

/**
 * Recompute `priority` per driver (1..n); unassigned rows keep priority 0. Same as TimeFilter `handleFinalPublish`.
 */
export function reprioritizePublishPayloadData(data) {
  const byDriver = new Map();
  data.forEach((item) => {
    if (item.status === 'unassigned') {
      item.priority = 0;
      return;
    }
    const driverKey = item.driver?._id ?? 'no-driver';
    if (!byDriver.has(driverKey)) byDriver.set(driverKey, []);
    byDriver.get(driverKey).push(item);
  });
  byDriver.forEach((items) => {
    items.forEach((item, idx) => {
      item.priority = idx + 1;
    });
  });
  return data;
}

function tripPublishId(trip) {
  const id = trip?.id ?? trip?._id;
  if (id == null || id === '') return null;
  return String(id);
}

function routeTripMatchesStep(trip, stepTripId) {
  const want = stepTripId == null ? '' : String(stepTripId);
  if (!want) return false;
  return [trip?.trip_id, trip?.id, trip?._id]
    .filter((v) => v != null && v !== '')
    .map((v) => String(v))
    .includes(want);
}

function normalizeRouteStopType(step) {
  const raw = step?.stop_type ?? step?.stopType ?? step?.type ?? '';
  const normalized = String(raw).replace(/[_\s-]/g, '').toLowerCase();
  if (normalized === 'pickup' || normalized === 'pick') return 'pickup';
  if (normalized === 'dropoff' || normalized === 'drop') return 'dropoff';
  return null;
}

function addBucketOrderRows(trips, validSet, output, startOrder, driverInfo) {
  let order = startOrder;
  (trips || []).forEach((trip) => {
    if (trip?.is_busy_trip || trip?.is_break) return;
    const id = tripPublishId(trip);
    if (!id || !validSet.has(id)) return;
    output.push({
      selectedTrips: [id],
      pickupOrder: order,
      dropOffOrder: order + 1,
      driver: driverInfo,
      type: 'dnd',
    });
    order += 2;
  });
  return order;
}

function addMultiloadRouteRows(section, routeSteps, validSet, output, startOrder, driverInfo) {
  if (!Array.isArray(routeSteps) || routeSteps.length === 0) {
    return { order: addBucketOrderRows(section.trips, validSet, output, startOrder, driverInfo), usedRoute: false };
  }

  const rowsById = new Map();
  const orderedSteps = [...routeSteps].sort((a, b) => {
    const aSeq = Number(a?.sequence);
    const bSeq = Number(b?.sequence);
    if (Number.isFinite(aSeq) && Number.isFinite(bSeq)) return aSeq - bSeq;
    return 0;
  });
  orderedSteps.forEach((step, stepIndex) => {
    const stopType = normalizeRouteStopType(step);
    const trip = (section.trips || []).find((t) => routeTripMatchesStep(t, step?.trip_id));
    const id = tripPublishId(trip);
    if (!stopType || !trip || !id || !validSet.has(id) || trip.is_busy_trip || trip.is_break) return;
    const row = rowsById.get(id) || {
      selectedTrips: [id],
      driver: driverInfo,
      type: 'dnd',
    };
    const order = startOrder + stepIndex;
    if (stopType === 'pickup') {
      row.pickupOrder = order;
    } else if (stopType === 'dropoff') {
      row.dropOffOrder = order;
    }
    rowsById.set(id, row);
  });

  const completeRows = Array.from(rowsById.values()).filter(
    (row) => row.pickupOrder != null && row.dropOffOrder != null
  );
  const expectedIds = (section.trips || [])
    .filter((trip) => !trip?.is_busy_trip && !trip?.is_break)
    .map(tripPublishId)
    .filter((id) => id && validSet.has(id));

  if (completeRows.length !== expectedIds.length) {
    return { order: addBucketOrderRows(section.trips, validSet, output, startOrder, driverInfo), usedRoute: false };
  }

  completeRows.forEach((row) => output.push(row));
  return { order: startOrder + orderedSteps.length, usedRoute: true };
}

function buildDriverLineRows(driver, multiloadRoutes, validSet) {
  const output = [];
  let order = 0;
  const driverInfo = {
    _id: driver?.driver_id ?? driver?.id ?? driver?._id,
    displayName: driver?.driver_name ?? driver?.displayName ?? '',
  };
  const trips = driver?.trips || [];
  const sections = buildTripSections(sortTripsForDisplay(trips || []));

  sections.forEach((section) => {
    if (section.multiloadGroupId) {
      const result = addMultiloadRouteRows(
        section,
        multiloadRoutes?.[section.multiloadGroupId],
        validSet,
        output,
        order,
        driverInfo
      );
      order = result.order;
      return;
    }
    order = addBucketOrderRows(section.trips, validSet, output, order, driverInfo);
  });

  return output;
}

function validTripIdSetFromPublishData(publishData) {
  const validSet = new Set();
  (publishData || []).forEach((item) => {
    if (item?.status === 'unassigned') return;
    (item?.selectedTrips || []).forEach((id) => {
      if (id != null && id !== '') validSet.add(String(id));
    });
  });
  return validSet;
}

/**
 * Build route line-order payload for /assigns/lines after validation.
 * Orders restart per driver bucket, then all driver rows are concatenated.
 */
export function buildAssignLinesOrderPayload(assignmentData, publishData, options = {}) {
  const validSet = validTripIdSetFromPublishData(publishData);
  if (!assignmentData || validSet.size === 0) return { data: [] };

  const publishType = options.publishType || 'planner';
  const onlyDriverId = options.onlyDriverId != null && options.onlyDriverId !== ''
    ? String(options.onlyDriverId)
    : null;
  const data = [];

  if (publishType === 'portal') {
    const op = assignmentData.original_plan;
    (op?.drivers || []).forEach((bucket) => {
      const dr = bucket.driver || {};
      if (onlyDriverId && !portalDriverMatchesId(dr, onlyDriverId)) return;
      const rows = [];
      addBucketOrderRows(bucket.trips || [], validSet, rows, 0, {
        _id: dr._id ?? dr.id,
        displayName: dr.displayName ?? dr.driver_name ?? '',
      });
      data.push(...rows);
    });
    return { data };
  }

  (assignmentData.drivers || []).forEach((driver) => {
    const driverId = String(driver?.driver_id ?? driver?.id ?? driver?._id ?? '');
    if (onlyDriverId && driverId !== onlyDriverId) return;
    data.push(...buildDriverLineRows(driver, assignmentData.multiload_routes || {}, validSet));
  });

  return { data };
}

/**
 * Single-driver slice of the main Publish "Planner" payload (TimeFilter `buildPlannerPayload` shape).
 * Used for Bucket view per-driver export—planner (optimized) assignment only, never Portal.
 */
export function buildPlannerSingleDriverPayload(assignmentData, driver) {
  const payload = { data: [] };
  if (!assignmentData) return payload;
  const want = String(driver?.driver_id ?? driver?.id ?? driver?._id ?? '');
  const row =
    (assignmentData.drivers || []).find((d) => String(d.driver_id ?? d.id ?? d._id) === want) || driver;
  const trips = row.trips || [];
  trips.forEach((trip, index) => {
    if (trip.is_busy_trip || trip.is_break) return;
    const tid = trip.id ?? trip.trip_id;
    if (tid == null || tid === '') return;
    payload.data.push({
      selectedTrips: [tid],
      status: 'offered',
      priority: index + 1,
      assignThrough: 'planner',
      driver: {
        _id: row.driver_id,
        displayName: row.driver_name,
        profileImageURL: row.profileImageURL || '',
      },
    });
  });
  return payload;
}
